import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import { COLORS, SPACING, FONT_SIZE } from '../constants/theme';

/**
 * RoutineAssignmentForm Component
 *
 * Allows coaches to assign entire drill routines to players as one-time assignments.
 * Supports three targeting modes: Team, Tag, or Individual Players.
 * Includes homework workload protection to prevent player overload.
 *
 * @param {Object} props
 * @param {Object} props.profile - Coach profile with organization_id
 * @param {Array} props.teams - Available teams for targeting
 * @param {Function} props.onClose - Callback to close the form
 * @param {Function} props.onSuccess - Callback after successful assignment
 */
const RoutineAssignmentForm = ({ profile, teams, onClose, onSuccess }) => {
  const { isDesktop } = useResponsive();

  // Form state
  const [routines, setRoutines] = useState([]);
  const [tags, setTags] = useState([]);
  const [players, setPlayers] = useState([]);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [targetType, setTargetType] = useState('team'); // team, tag, players
  const [targetTeam, setTargetTeam] = useState('');
  const [targetTag, setTargetTag] = useState('');
  const [targetPlayers, setTargetPlayers] = useState([]);
  const [dueDate, setDueDate] = useState('');
  const [coachNotes, setCoachNotes] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [workloadWarning, setWorkloadWarning] = useState('');
  const [targetPlayerIds, setTargetPlayerIds] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // When target changes, resolve to player IDs and check workload
    resolveTargetPlayers();
  }, [targetType, targetTeam, targetTag, targetPlayers]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load routines
      const { data: routinesData, error: routinesError } = await supabase
        .from('drill_templates')
        .select('id, name, description, created_at')
        .eq('organization_id', profile.organization_id)
        .order('name');

      if (routinesError) throw routinesError;
      setRoutines(routinesData || []);

      // Load tags
      const { data: tagsData, error: tagsError } = await supabase
        .from('player_tags')
        .select('id, name, color')
        .eq('organization_id', profile.organization_id)
        .order('name');

      if (tagsError) throw tagsError;
      setTags(tagsData || []);

      // Load all players in organization for individual selection
      const { data: playersData, error: playersError } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .eq('organization_id', profile.organization_id)
        .eq('role', 'player')
        .order('full_name');

      if (playersError) throw playersError;
      setPlayers(playersData || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resolveTargetPlayers = async () => {
    try {
      setWorkloadWarning('');
      setTargetPlayerIds([]);

      let playerIds = [];

      if (targetType === 'team' && targetTeam) {
        // Get players on selected team
        const { data, error } = await supabase
          .from('profiles')
          .select('id')
          .eq('team_id', targetTeam)
          .eq('role', 'player');

        if (error) throw error;
        playerIds = data.map((p) => p.id);
      } else if (targetType === 'tag' && targetTag) {
        // Get players with selected tag
        const { data, error } = await supabase
          .from('player_tag_assignments')
          .select('player_id')
          .eq('tag_id', targetTag);

        if (error) throw error;
        playerIds = data.map((p) => p.player_id);
      } else if (targetType === 'players' && targetPlayers.length > 0) {
        // Use selected player IDs directly
        playerIds = [...targetPlayers];
      }

      setTargetPlayerIds(playerIds);

      // Check workload for each player
      if (playerIds.length > 0) {
        await checkWorkload(playerIds);
      }
    } catch (err) {
      console.error('Error resolving target players:', err);
      setError(err.message);
    }
  };

  const checkWorkload = async (playerIds) => {
    try {
      const overloadedPlayers = [];

      for (const playerId of playerIds) {
        const { data, error } = await supabase.rpc('get_player_homework_minutes', {
          p_player_id: playerId,
        });

        if (error) throw error;

        if (data > 120) {
          // Get player name for warning
          const player = players.find((p) => p.id === playerId);
          overloadedPlayers.push({
            name: player?.full_name || 'Unknown',
            minutes: data,
          });
        }
      }

      if (overloadedPlayers.length > 0) {
        const warningText =
          overloadedPlayers.length === 1
            ? `⚠️ ${overloadedPlayers[0].name} has ${overloadedPlayers[0].minutes} minutes of pending homework. Consider reducing workload.`
            : `⚠️ ${overloadedPlayers.length} players have >120 minutes of pending homework. Consider reducing workload.`;
        setWorkloadWarning(warningText);
      }
    } catch (err) {
      console.error('Error checking workload:', err);
    }
  };

  const handleRoutineChange = async (routineId) => {
    const routine = routines.find((r) => r.id === routineId);
    setSelectedRoutine(routine);
    setShowPreview(false);
  };

  const handleTargetTypeChange = (type) => {
    setTargetType(type);
    setTargetTeam('');
    setTargetTag('');
    setTargetPlayers([]);
    setWorkloadWarning('');
  };

  const handlePlayerToggle = (playerId) => {
    setTargetPlayers((prev) =>
      prev.includes(playerId) ? prev.filter((id) => id !== playerId) : [...prev, playerId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      // Validation
      if (!selectedRoutine) {
        throw new Error('Please select a routine');
      }

      if (targetPlayerIds.length === 0) {
        throw new Error('No players selected. Please select at least one target.');
      }

      if (!dueDate) {
        throw new Error('Please select a due date');
      }

      // Validate due date is not in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(dueDate);
      if (selectedDate < today) {
        throw new Error('Due date cannot be in the past');
      }

      // Get routine drills
      const { data: routineDrills, error: drillsError } = await supabase
        .from('drill_template_items')
        .select(
          `
          drill_id,
          sets,
          reps,
          difficulty_level,
          coach_notes,
          drill:drills(name, duration_minutes)
        `
        )
        .eq('template_id', selectedRoutine.id)
        .order('order_index');

      if (drillsError) throw drillsError;

      if (!routineDrills || routineDrills.length === 0) {
        throw new Error('This routine has no drills. Please add drills to the routine first.');
      }

      // Create assignments for each player
      const assignments = [];
      for (const playerId of targetPlayerIds) {
        for (const item of routineDrills) {
          const routineNote = `[From routine: ${selectedRoutine.name}]`;
          const combinedNotes = [coachNotes, item.coach_notes, routineNote]
            .filter(Boolean)
            .join('\n\n');

          assignments.push({
            player_id: playerId,
            drill_id: item.drill_id,
            assigned_by: profile.id,
            template_id: selectedRoutine.id,
            due_date: dueDate,
            sets: item.sets,
            reps: item.reps,
            difficulty_level: item.difficulty_level,
            coach_notes: combinedNotes,
            completed: false,
          });
        }
      }

      // Insert all assignments
      const { error: assignError } = await supabase.from('assigned_drills').insert(assignments);

      if (assignError) throw assignError;

      // Success!
      const totalAssignments = assignments.length;
      const totalPlayers = targetPlayerIds.length;
      const totalDrills = routineDrills.length;

      if (onSuccess) {
        onSuccess({
          message: `Successfully assigned "${selectedRoutine.name}" (${totalDrills} drills) to ${totalPlayers} player${totalPlayers > 1 ? 's' : ''} (${totalAssignments} total assignments created)`,
          routine: selectedRoutine,
          playerCount: totalPlayers,
          drillCount: totalDrills,
          assignmentCount: totalAssignments,
        });
      }

      onClose();
    } catch (err) {
      console.error('Error assigning routine:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Styles
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: isDesktop ? SPACING.xl : SPACING.md,
  };

  const modalStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  };

  const headerStyle = {
    padding: SPACING.lg,
    borderBottom: `1px solid ${COLORS.gray[200]}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const titleStyle = {
    fontSize: FONT_SIZE.xl,
    fontWeight: '600',
    color: COLORS.gray[900],
    margin: 0,
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    fontSize: FONT_SIZE.xl,
    color: COLORS.gray[600],
    cursor: 'pointer',
    padding: SPACING.sm,
  };

  const bodyStyle = {
    padding: SPACING.lg,
  };

  const formGroupStyle = {
    marginBottom: SPACING.lg,
  };

  const labelStyle = {
    display: 'block',
    fontSize: FONT_SIZE.sm,
    fontWeight: '500',
    color: COLORS.gray[700],
    marginBottom: SPACING.sm,
  };

  const selectStyle = {
    width: '100%',
    padding: SPACING.md,
    fontSize: FONT_SIZE.base,
    border: `1px solid ${COLORS.gray[300]}`,
    borderRadius: '4px',
    backgroundColor: 'white',
    minHeight: '44px',
  };

  const inputStyle = {
    width: '100%',
    padding: SPACING.md,
    fontSize: FONT_SIZE.base,
    border: `1px solid ${COLORS.gray[300]}`,
    borderRadius: '4px',
    minHeight: '44px',
    boxSizing: 'border-box',
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: '100px',
    fontFamily: 'inherit',
    resize: 'vertical',
  };

  const radioGroupStyle = {
    display: 'flex',
    flexDirection: isDesktop ? 'row' : 'column',
    gap: SPACING.md,
    marginTop: SPACING.sm,
  };

  const radioLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    padding: SPACING.md,
    border: `2px solid ${COLORS.gray[300]}`,
    borderRadius: '4px',
    flex: isDesktop ? '1' : 'auto',
    minHeight: '44px',
  };

  const radioLabelActiveStyle = {
    ...radioLabelStyle,
    borderColor: COLORS.primary,
    backgroundColor: '#e3f2fd', // Light blue
  };

  const buttonStyle = {
    padding: `${SPACING.md} ${SPACING.lg}`,
    fontSize: FONT_SIZE.base,
    fontWeight: '500',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    minHeight: '44px',
    transition: 'background-color 0.2s',
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: COLORS.primary,
    color: 'white',
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: COLORS.gray[300],
    color: COLORS.gray[700],
  };

  const successButtonStyle = {
    ...buttonStyle,
    backgroundColor: COLORS.success,
    color: 'white',
  };

  const alertStyle = (type) => ({
    padding: SPACING.md,
    borderRadius: '4px',
    marginBottom: SPACING.md,
    fontSize: FONT_SIZE.sm,
    ...(type === 'error' && {
      backgroundColor: COLORS.alert.error.bg,
      color: COLORS.alert.error.text,
      border: `1px solid ${COLORS.alert.error.border}`,
    }),
    ...(type === 'warning' && {
      backgroundColor: '#fff3e0',
      color: '#f57c00',
      border: '1px solid #ffcc80',
    }),
    ...(type === 'info' && {
      backgroundColor: '#e3f2fd',
      color: '#1976d2',
      border: '1px solid #90caf9',
    }),
  });

  const checkboxListStyle = {
    maxHeight: '200px',
    overflowY: 'auto',
    border: `1px solid ${COLORS.gray[300]}`,
    borderRadius: '4px',
    padding: SPACING.sm,
  };

  const checkboxItemStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: SPACING.sm,
    minHeight: '44px',
    cursor: 'pointer',
  };

  const previewStyle = {
    backgroundColor: COLORS.gray[50],
    padding: SPACING.md,
    borderRadius: '4px',
    border: `1px solid ${COLORS.gray[200]}`,
  };

  const footerStyle = {
    padding: SPACING.lg,
    borderTop: `1px solid ${COLORS.gray[200]}`,
    display: 'flex',
    gap: SPACING.md,
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  };

  if (loading) {
    return (
      <div style={modalOverlayStyle}>
        <div style={modalStyle}>
          <div style={bodyStyle}>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>Assign Routine</h2>
          <button style={closeButtonStyle} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={bodyStyle}>
            {error && <div style={alertStyle('error')}>{error}</div>}

            {workloadWarning && <div style={alertStyle('warning')}>{workloadWarning}</div>}

            {/* Routine Selection */}
            <div style={formGroupStyle}>
              <label style={labelStyle}>Select Routine *</label>
              <select
                style={selectStyle}
                value={selectedRoutine?.id || ''}
                onChange={(e) => handleRoutineChange(e.target.value)}
                required
              >
                <option value="">Choose a routine...</option>
                {routines.map((routine) => (
                  <option key={routine.id} value={routine.id}>
                    {routine.name}
                  </option>
                ))}
              </select>
              {selectedRoutine && (
                <div style={{ marginTop: SPACING.sm }}>
                  <button
                    type="button"
                    style={{ ...secondaryButtonStyle, fontSize: FONT_SIZE.sm }}
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    {showPreview ? 'Hide' : 'Preview'} Routine Details
                  </button>
                </div>
              )}
            </div>

            {/* Routine Preview */}
            {showPreview && selectedRoutine && (
              <div style={{ ...previewStyle, ...formGroupStyle }}>
                <p style={{ margin: 0, fontSize: FONT_SIZE.sm, color: COLORS.gray[600] }}>
                  {selectedRoutine.description || 'No description'}
                </p>
              </div>
            )}

            {/* Target Type Selection */}
            <div style={formGroupStyle}>
              <label style={labelStyle}>Assign To *</label>
              <div style={radioGroupStyle}>
                <label style={targetType === 'team' ? radioLabelActiveStyle : radioLabelStyle}>
                  <input
                    type="radio"
                    name="targetType"
                    value="team"
                    checked={targetType === 'team'}
                    onChange={() => handleTargetTypeChange('team')}
                    style={{ marginRight: SPACING.sm }}
                  />
                  Team
                </label>
                <label style={targetType === 'tag' ? radioLabelActiveStyle : radioLabelStyle}>
                  <input
                    type="radio"
                    name="targetType"
                    value="tag"
                    checked={targetType === 'tag'}
                    onChange={() => handleTargetTypeChange('tag')}
                    style={{ marginRight: SPACING.sm }}
                  />
                  Tag
                </label>
                <label style={targetType === 'players' ? radioLabelActiveStyle : radioLabelStyle}>
                  <input
                    type="radio"
                    name="targetType"
                    value="players"
                    checked={targetType === 'players'}
                    onChange={() => handleTargetTypeChange('players')}
                    style={{ marginRight: SPACING.sm }}
                  />
                  Individual Players
                </label>
              </div>
            </div>

            {/* Team Selection */}
            {targetType === 'team' && (
              <div style={formGroupStyle}>
                <label style={labelStyle}>Select Team *</label>
                <select
                  style={selectStyle}
                  value={targetTeam}
                  onChange={(e) => setTargetTeam(e.target.value)}
                  required
                >
                  <option value="">Choose a team...</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Tag Selection */}
            {targetType === 'tag' && (
              <div style={formGroupStyle}>
                <label style={labelStyle}>Select Tag *</label>
                <select
                  style={selectStyle}
                  value={targetTag}
                  onChange={(e) => setTargetTag(e.target.value)}
                  required
                >
                  <option value="">Choose a tag...</option>
                  {tags.map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Individual Player Selection */}
            {targetType === 'players' && (
              <div style={formGroupStyle}>
                <label style={labelStyle}>Select Players * (at least one)</label>
                <div style={checkboxListStyle}>
                  {players.map((player) => (
                    <label key={player.id} style={checkboxItemStyle}>
                      <input
                        type="checkbox"
                        checked={targetPlayers.includes(player.id)}
                        onChange={() => handlePlayerToggle(player.id)}
                        style={{ marginRight: SPACING.sm }}
                      />
                      {player.full_name} ({player.email})
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Target Summary */}
            {targetPlayerIds.length > 0 && (
              <div style={alertStyle('info')}>
                {targetPlayerIds.length} player{targetPlayerIds.length > 1 ? 's' : ''} selected
              </div>
            )}

            {/* Due Date */}
            <div style={formGroupStyle}>
              <label style={labelStyle}>Due Date *</label>
              <input
                type="date"
                style={inputStyle}
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            {/* Coach Notes */}
            <div style={formGroupStyle}>
              <label style={labelStyle}>Coach Notes (Optional)</label>
              <textarea
                style={textareaStyle}
                value={coachNotes}
                onChange={(e) => setCoachNotes(e.target.value)}
                placeholder="Add any additional instructions or context for this assignment..."
              />
            </div>
          </div>

          {/* Footer */}
          <div style={footerStyle}>
            <button
              type="button"
              style={secondaryButtonStyle}
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={successButtonStyle}
              disabled={submitting || targetPlayerIds.length === 0 || !selectedRoutine}
            >
              {submitting ? 'Assigning...' : 'Assign Routine'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoutineAssignmentForm;
